import { type SVGProps, useEffect, useId, useMemo, useRef, useState } from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'

import { cn } from '@/lib/utils'

const NONE = '__shopora_no_category__'

function ChevronDown(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  )
}

function ChevronUp(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
    </svg>
  )
}

function Check(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden {...props}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  )
}

export type CategoryOption = {
  id: number | null
  slug: string
  name: string
}

export type CategorySearchSelectProps = {
  id?: string
  /** Stored category slug, or '' for none */
  value: string
  onValueChange: (slug: string) => void
  categories: CategoryOption[]
  disabled?: boolean
  className?: string
  'aria-invalid'?: boolean
}

/**
 * Assignable store categories with a search field to filter by name or slug (Add/Edit product).
 */
export function CategorySearchSelect({
  id,
  value,
  onValueChange,
  categories,
  disabled = false,
  className,
  'aria-invalid': ariaInvalid,
}: CategorySearchSelectProps) {
  const searchId = useId()
  const searchRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const selectedInList = categories.some((c) => c.slug === value)
  const radixValue = !value || !selectedInList ? NONE : value

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return categories
    return categories.filter(
      (c) => c.name.toLowerCase().includes(q) || c.slug.toLowerCase().includes(q),
    )
  }, [categories, query])

  useEffect(() => {
    if (!open) setQuery('')
    else requestAnimationFrame(() => searchRef.current?.focus())
  }, [open])

  return (
    <SelectPrimitive.Root
      value={radixValue}
      onValueChange={(v) => onValueChange(v === NONE ? '' : v)}
      open={open}
      onOpenChange={setOpen}
      disabled={disabled}
    >
      <SelectPrimitive.Trigger
        id={id}
        aria-invalid={ariaInvalid}
        className={cn(
          'flex w-full cursor-pointer items-center justify-between gap-2 rounded-2xl border border-border bg-card px-4 py-2.5 text-left text-sm text-foreground shadow-sm outline-none transition-all',
          'hover:border-border focus:border-ring focus:ring-2 focus:ring-ring/30',
          'disabled:cursor-not-allowed disabled:opacity-60',
          'data-[placeholder]:text-muted-foreground [&_svg]:shrink-0',
          ariaInvalid && 'border-destructive ring-destructive/20',
          className,
        )}
      >
        <SelectPrimitive.Value placeholder="No category" />
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="size-4 opacity-50" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={6}
          className={cn(
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 z-50 max-h-[min(22rem,calc(100vh-4rem))] w-[var(--radix-select-trigger-width)] overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-md',
            'data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1',
          )}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <SelectPrimitive.ScrollUpButton className="flex cursor-default items-center justify-center py-1 text-muted-foreground">
            <ChevronUp className="size-4" />
          </SelectPrimitive.ScrollUpButton>

          <div
            className="border-b border-border bg-popover p-2"
            onPointerDown={(e) => e.stopPropagation()}
          >
            <label htmlFor={searchId} className="sr-only">
              Search categories
            </label>
            <input
              ref={searchRef}
              id={searchId}
              type="search"
              autoComplete="off"
              placeholder="Search categories…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
            />
          </div>

          <SelectPrimitive.Viewport className="max-h-[min(240px,calc(100vh-12rem))] overflow-y-auto p-1">
            <SelectPrimitive.Item
              value={NONE}
              className={cn(
                'relative flex w-full cursor-default select-none items-center rounded-md py-2 pr-8 pl-2 text-sm outline-none',
                'focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
              )}
            >
              <span className="absolute right-2 flex size-3.5 items-center justify-center">
                <SelectPrimitive.ItemIndicator>
                  <Check className="size-4" />
                </SelectPrimitive.ItemIndicator>
              </span>
              <SelectPrimitive.ItemText>No category</SelectPrimitive.ItemText>
            </SelectPrimitive.Item>

            {filtered.map((c) => (
              <SelectPrimitive.Item
                key={c.id ?? c.slug}
                value={c.slug}
                className={cn(
                  'relative flex w-full cursor-default select-none items-center rounded-md py-2 pr-8 pl-2 text-sm outline-none',
                  'focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                )}
              >
                <span className="absolute right-2 flex size-3.5 items-center justify-center">
                  <SelectPrimitive.ItemIndicator>
                    <Check className="size-4" />
                  </SelectPrimitive.ItemIndicator>
                </span>
                <SelectPrimitive.ItemText>
                  {c.name} ({c.slug})
                </SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ))}

            {filtered.length === 0 && categories.length > 0 ? (
              <div className="px-2 py-3 text-center text-sm text-muted-foreground">
                No categories match your search.
              </div>
            ) : null}
          </SelectPrimitive.Viewport>

          <SelectPrimitive.ScrollDownButton className="flex cursor-default items-center justify-center py-1 text-muted-foreground">
            <ChevronDown className="size-4" />
          </SelectPrimitive.ScrollDownButton>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  )
}
